import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchRoles, fetchPermissions, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Pagination } from "@/components/ui/Pagination";
import { useTableState } from "@/lib/useTableState";

// Permissions are grouped per-category in the UI, not paginated.
// Pull a large page so the grouped catalog renders in full.
const PERMS_PAGE = { limit: 200, offset: 0 } as const;

export const Route = createFileRoute("/_app/admins/roles")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: keys.rolesList(20, 0, { by: "name", dir: "asc" }),
        queryFn: () => fetchRoles({ limit: 20, offset: 0, sort: { by: "name", dir: "asc" } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: keys.permissionsList(PERMS_PAGE.limit, PERMS_PAGE.offset),
        queryFn: () => fetchPermissions(PERMS_PAGE),
      }),
    ]),
  component: RolesPage,
});

function RolesPage() {
  // Roles is a small list — no filters needed, just sort + paginate.
  const table = useTableState({
    defaultSort: { by: "name", dir: "asc" },
  });
  const roles = useQuery({
    queryKey: keys.rolesList(table.limit, table.offset, table.sort),
    queryFn: () =>
      fetchRoles({ limit: table.limit, offset: table.offset, sort: table.sort }),
  });
  const perms = useQuery({
    queryKey: keys.permissionsList(PERMS_PAGE.limit, PERMS_PAGE.offset),
    queryFn: () => fetchPermissions(PERMS_PAGE),
  });

  // Group permissions by category for the catalog view.
  type Perm = { permission_id: string; code: string; description: string; category: string };
  const grouped = (perms.data?.items ?? []).reduce<Record<string, Perm[]>>(
    (acc, p) => { (acc[p.category] ||= []).push(p); return acc; }, {},
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Roles</CardTitle>
          <span className="text-2xs text-muted">{roles.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <ul className="divide-y divide-border/60">
            {(roles.data?.items ?? []).map((r) => (
              <li key={r.role_id} className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-fg">{r.name}</span>
                  {r.is_builtin && <Badge tone="muted">built-in</Badge>}
                </div>
                <div className="mt-0.5 text-2xs text-muted">{r.description}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {r.permission_codes.map((c) => (
                    <span key={c} className="rounded bg-surface-2 px-1.5 py-0.5 text-2xs font-mono text-muted">{c}</span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {roles.data && (
            <Pagination
              page={roles.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="roles"
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Permissions catalog</CardTitle>
          <span className="text-2xs text-muted">{perms.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="border-b border-border/60 last:border-0">
              <div className="bg-surface-2 px-5 py-2 text-2xs uppercase tracking-wide text-muted">{cat}</div>
              <ul>
                {list.map((p) => (
                  <li key={p.permission_id} className="grid grid-cols-[1fr_2fr] gap-3 px-5 py-1.5 text-2xs">
                    <span className="font-mono text-fg">{p.code}</span>
                    <span className="text-muted">{p.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}
